/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService, BaseServiceRegion } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';
import { ComponentContentServiceConfiguration } from './ComponentContent.service.config';
import {
  ComponentContentServicePatchMatrixIdentifierSettingsDto,
  ComponentContentServicePostMatrixIdentifierSettingsDto,
} from './ComponentContent.service.dto';
import { EntityMatrixIdentifier } from './Entities/MatrixIdentifier/MatrixIdentifier.entity';

export class ComponentContentService extends BaseService {
  public matrixIdentifiers$ = new BehaviorSubject<Array<EntityMatrixIdentifier>>([]);

  constructor(configuration: ComponentContentServiceConfiguration) {
    super(configuration);
  }

  private getContentServiceUrl(): string | null {
    // Note: Region is set as `au` for now
    return this.getServiceUrlByRegion(`components-content`, BaseServiceRegion.au);
  }

  /**
   * Creates url to interact with a single matrix identifier.
   * @param {Pick<EntityMatrixIdentifier, 'name'>} dto Matrix identifier with primary key.
   * @returns {string} The url to interact with a single matrix identifier.
   */
  private createSingleMatrixIdentifierUrl(dto: Pick<EntityMatrixIdentifier, 'name'>): string {
    const { name } = dto;
    const url = this.getContentServiceUrl();

    return `${url}/settings/matrix-identifiers/${name}`;
  }

  /**
   * Creates url to get all matrix identifiers.
   * @returns {string} The url to get all matrix identifiers.
   */
  private createAllMatrixIdentifiersUrl(): string {
    const url = this.getContentServiceUrl();

    return `${url}/settings/matrix-identifiers`;
  }

  /**
   * Attempt to fetch all matrix identifier.
   * @returns {Observable<Array<EntityMatrixIdentifier>>} The list of matrix identifier settings.
   */
  getAllMatrixIdentifiers(): Observable<Array<EntityMatrixIdentifier>> {
    const url = this.createAllMatrixIdentifiersUrl();
    return this.httpService.get<unknown, HttpResponse>(url, this.getSessionRequestOptions()).pipe(
      switchMap((value) => {
        return this._handleResponseError<Array<EntityMatrixIdentifier>>(value);
      }),
      tap((value) => {
        this.matrixIdentifiers$.next(value);
      }),
    );
  }

  /**
   * Attempt to update a single matrix identifier.
   * @param {ComponentContentServicePatchMatrixIdentifierSettingsDto} dto Matrix identifier update object.
   * @returns {Observable<EntityMatrixIdentifier>} The updated matrix identifier.
   */
  updateMatrixIdentifier(
    dto: ComponentContentServicePatchMatrixIdentifierSettingsDto,
  ): Observable<EntityMatrixIdentifier> {
    const url = this.createSingleMatrixIdentifierUrl(dto);
    return this.httpService
      .patch<unknown, HttpResponse>(url, {
        body: dto.updates,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value) => {
          return this._handleResponseError<EntityMatrixIdentifier>(value);
        }),
      );
  }

  /**
   * Attempt to create a single matrix identifier.
   * @param {ComponentContentServicePostMatrixIdentifierSettingsDto} dto Matrix identifier create object.
   * @returns {Observable<EntityMatrixIdentifier>} The created matrix identifier.
   */
  createMatrixIdentifier(
    dto: ComponentContentServicePostMatrixIdentifierSettingsDto,
  ): Observable<EntityMatrixIdentifier> {
    const url = this.createAllMatrixIdentifiersUrl();
    return this.httpService
      .post<unknown, HttpResponse>(url, {
        body: dto,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value) => {
          return this._handleResponseError<EntityMatrixIdentifier>(value);
        }),
      );
  }

  /**
   * Attempt to delete a single matrix identifier.
   * @param {string} identifierName Matrix identifier name.
   * @returns {Observable<unknown>} An empty observable.
   */
  deleteMatrixIdentifier(identifierName: string): Observable<unknown> {
    const url = this.createSingleMatrixIdentifierUrl({ name: identifierName });
    return this.httpService.delete<unknown, HttpResponse>(url, this.getSessionRequestOptions()).pipe(
      switchMap((value) => {
        return this._handleResponseError<unknown>(value);
      }),
    );
  }
}
